import {
  getCurrentBrand,
  getPortfolioBrands,
  getSelectedPortfolioBrands,
} from '../../services/dashboard/portfolioBrandsService';
import { getDefaultDashboardDates } from '../../services/dashboard/dates/datesService';
import { getAvailableReportFields } from '../../services/dashboard/reportFieldService';
import { getAllReports } from '../../services/dashboard/scheduledReportService';
import {
  getCategories,
  getSubcategories,
  getBenchmarksBrands,
} from '../../services/dashboard/benchmarksService';

const state = {
  reportRequest: {
    startDate: undefined,
    endDate: undefined,
    predefinedRangeValue: 0,
    comparisonRange: {},
    metrics: [],
    dimensions: [],
    demographicsFilters: [],
    demographicsMetrics: [],
    demographicsDimension: '',
    demographicsFilterType: 'Basic',
    demographicsIncludeFullSegments: false,
  },
  availableReportFields: {
    allReportFields: undefined,
    dimensions: undefined,
    demographicsDimensions: undefined,
    allSegmentsDimension: undefined,
    metrics: undefined,
  },
  userSavedReports: [],
  portfolioBrands: [],
  insightsData: undefined,
  explorerData: undefined,
  salesInsightsData: undefined,
  demographicsData: undefined,
  demographicsOverviewData: undefined,
  currentDashboardReportId: undefined,
  currentDemographicsReportId: undefined,
  categoriesAndSubcategories: undefined,
  selectedCategory: undefined,
  selectedSubcategories: [],
};

const mutations = {
  updateInsightsData(state, data) {
    state.insightsData = data;
  },
  updateSalesInsightsData(state, data) {
    state.salesInsightsData = data;
  },
  updateExplorerData(state, data) {
    state.explorerData = data;
  },
  updateDemographicsData(state, data) {
    state.demographicsData = data;
  },
  updateDemographicsOverviewData(state, data) {
    state.demographicsOverviewData = data;
  },
  updateCurrentDashboardReportId(state, dashboardReportId) {
    state.currentDashboardReportId = dashboardReportId;
  },
  updatePredefinedRangeValue(state, rangeValue) {
    state.reportRequest.predefinedRangeValue = rangeValue;
  },
  updateReportStartDate(state, startDate) {
    state.reportRequest.startDate = startDate;
  },
  updateReportEndDate(state, endDate) {
    state.reportRequest.endDate = endDate;
  },
  updateReportComparisonRange(state, comparisonRange) {
    state.reportRequest.comparisonRange = comparisonRange;
  },
  updateReportMetrics(state, metrics) {
    state.reportRequest.metrics = metrics;
  },
  updateReportDimensions(state, dimensions) {
    state.reportRequest.dimensions = dimensions;
  },
  updateDemographicsFilters(state, filters) {
    state.reportRequest.demographicsFilters = filters;
  },
  updateDemographicsMetrics(state, metrics) {
    state.reportRequest.demographicsMetrics = metrics;
  },
  updateDemographicsDimension(state, dimension) {
    state.reportRequest.demographicsDimension = dimension;
  },
  updateDemographicsIncludeFullSegments(state, includeFullSegments) {
    state.reportRequest.demographicsIncludeFullSegments = includeFullSegments;
  },
  updateCurrentDemographicsReportId(state, demographicsReportId) {
    state.currentDemographicsReportId = demographicsReportId;
  },
  updateDemographicsFilterType(state, filterType) {
    state.reportRequest.demographicsFilterType = filterType;
  },
  updatePortfolioBrands(state, portfolioBrands) {
    state.portfolioBrands = portfolioBrands;
  },
  updateAvailableAllReportFields(state, allReportFields) {
    state.availableReportFields.allReportFields = allReportFields;
  },
  updateAvailableDimensions(state, dimensions) {
    state.availableReportFields.dimensions = dimensions;
  },
  updateAvailableDemographicsDimensions(state, demographicsDimensions) {
    state.availableReportFields.demographicsDimensions = demographicsDimensions;
  },
  updateAvailableMetrics(state, metrics) {
    state.availableReportFields.metrics = metrics;
  },
  updateAvailableAllSegmentsDimension(state, allSegmentsDimension) {
    state.availableReportFields.allSegmentsDimension = allSegmentsDimension;
  },
  updateUserSavedReports(state, reports) {
    state.userSavedReports = reports;
  },
  resetExplorerReport(state) {
    state.reportRequest.dimensions = [];
    state.reportRequest.metrics = [];
    state.explorerData = undefined;
  },
  updateCategoriesAndSubcategories(state, categoriesAndSubcategories) {
    state.categoriesAndSubcategories = categoriesAndSubcategories;
  },
  updateBenchmarksCategory(state, category) {
    state.selectedCategory = category;
  },
  updateBenchmarksSubcategories(state, subcategories) {
    state.selectedSubcategories = subcategories;
  },
};

const actions = {
  async setInitialDashboardData({ dispatch }, { token }) {
    await dispatch('setCategoriesAndSubcategories', token);
    await dispatch('setDefaultDashboardBrand', token);
    await dispatch('setDefaultDashboardDates');
    await dispatch('setAvailableReportFields', token);
    await dispatch('setAllowedFilters');
    await dispatch('setUserSavedReports', token);
  },
  async setPortfolioBrands({ commit, dispatch, rootState }, portfolioBrands) {
    const attachBrand = rootState.attachStudio.currentAttachBrand;

    const selectedPortfolioBrands = portfolioBrands.filter(b => b.checked);
    const selectedPortfolioBrandIds = selectedPortfolioBrands.map(b => b.brandId);

    const isSetDefaultBrandNeeded = attachBrand === undefined
      || attachBrand === null
      || !selectedPortfolioBrandIds.includes(attachBrand.brandId);

    if (isSetDefaultBrandNeeded) {
      const currentBrand = selectedPortfolioBrands[0];

      await dispatch('setCurrentBrandData', currentBrand);
    }

    commit('updatePortfolioBrands', portfolioBrands);
    await dispatch('setDisabledLinks');
  },
  async setCategoriesAndSubcategories({ commit }, token) {
    const categories = await getCategories(token);
    const subcategories = await getSubcategories(token);

    const categoriesAndSubcategories = {};

    for (const i in categories) {
      const { id, name } = categories[i];

      categoriesAndSubcategories[id] = {
        value: id,
        key: name,
        subcategories: [],
      };
    }

    for (const i in subcategories) {
      const { id, name, parent_category_id } = subcategories[i];

      categoriesAndSubcategories[id] = { value: id, key: name };

      if (categoriesAndSubcategories[parent_category_id]) {
        categoriesAndSubcategories[parent_category_id].subcategories.push(id);
      }
    }

    commit('updateCategoriesAndSubcategories', categoriesAndSubcategories);
  },
  async setDefaultDashboardBrand({ commit, dispatch, rootState }, token) {
    if (!state.portfolioBrands || state.portfolioBrands.length === 0) {
      await dispatch('setAllPortfolioBrands', token);
    }

    await dispatch('addBenchmarksKeyValues', token);

    const storedBrandId = window.localStorage.getItem('mmPlatformBrandIdFilter');

    const currentBrand = getCurrentBrand(
      rootState.main.currentUser,
      storedBrandId,
      rootState.attachStudio.currentAttachBrand,
      state.portfolioBrands,
    );

    const currentBrandIds = currentBrand === undefined || currentBrand === null
      ? []
      : [currentBrand.brandId];

    const selectedPortfolioBrands = getSelectedPortfolioBrands(
      state.portfolioBrands,
      currentBrandIds,
    );

    commit('updatePortfolioBrands', selectedPortfolioBrands);
    commit('updateBenchmarksCategory', currentBrand.categoryId);
    commit('updateBenchmarksSubcategories', currentBrand.subCategoryIds);
    await dispatch('setCurrentBrandData', currentBrand);
    await dispatch('setDisabledLinks');
  },
  setDefaultDashboardDates({ commit, rootState }) {
    const defaultBrand = rootState.attachStudio.currentAttachBrand;
    const { start, end, comparisonRange } = getDefaultDashboardDates(defaultBrand);

    commit('updateReportStartDate', start);
    commit('updateReportEndDate', end);
    commit('updateReportComparisonRange', comparisonRange);
  },
  async setAllPortfolioBrands({ commit, dispatch, rootState }, token) {
    if (!rootState.main.brands) {
      await dispatch('setAllBrandData', token);
    }

    const user = rootState.main.currentUser;
    const allBrandsMap = rootState.main.brands;

    const portfolioBrands = await getPortfolioBrands(user, allBrandsMap, token);

    commit('updatePortfolioBrands', portfolioBrands);
  },
  async addBenchmarksKeyValues({ commit }, token) {
    const portfolioBrands = state.portfolioBrands || [];
    const brandIds = portfolioBrands.map(b => b.brandId);

    if (brandIds.length > 0) {
      const benchmarksBrands = await getBenchmarksBrands({ brandIds }, token) || [];

      const benchmarksBrandsObj = {};

      for (const i in benchmarksBrands) {
        const { brandId } = benchmarksBrands[i];

        benchmarksBrandsObj[brandId] = benchmarksBrands[i];
      }

      portfolioBrands.forEach(pb => {
        const benchmarksInfo = benchmarksBrandsObj[pb.brandId] || {};

        Object.assign(pb, benchmarksInfo);
      });

      commit('updatePortfolioBrands', portfolioBrands);
    }
  },
  async setAvailableReportFields({ commit }, token) {
    const dimensions = await getAvailableReportFields({ type: 'dimensions' }, token);
    const demographicsDimensions = await getAvailableReportFields({
      type: 'demographicsDimensions',
    }, token);
    const allSegmentsDimension = (await getAvailableReportFields({ name: 'allSegments' }, token));
    const metrics = await getAvailableReportFields({ type: 'metrics' }, token);
    const allReportFields = dimensions.concat(demographicsDimensions, metrics);

    commit('updateAvailableDimensions', dimensions);
    commit('updateAvailableDemographicsDimensions', demographicsDimensions);
    commit('updateAvailableAllSegmentsDimension', allSegmentsDimension[0]);
    commit('updateAvailableMetrics', metrics);
    commit('updateAvailableAllReportFields', allReportFields);
  },
  async setUserSavedReports({ commit }, token) {
    try {
      const reports = await getAllReports(token);

      commit('updateUserSavedReports', reports.data);
    } catch (e) {
      if (e.status === 404) {
        commit('updateUserSavedReports', []);
      } else {
        return;
      }
    }
  },
};

const getters = {
  activeViewEnabledBrands(
    state,
    { demographicsEnabledPortfolioBrands, salesInsightsEnabledPortfolioBrands },
    { main = {} },
  ) {
    switch (main.activeView) {
      case 'Sales Insights':
        return salesInsightsEnabledPortfolioBrands;
      case 'Shopper Intelligence':
        return demographicsEnabledPortfolioBrands;
      default:
        return state.portfolioBrands;
    }
  },
  hasNonUSBrandSelected(state, { selectedBrands }) {
    return selectedBrands.some(({ brandCountryName }) => {
      return brandCountryName && brandCountryName !== 'United States';
    });
  },
  hasUSBrandSelected(state, { selectedBrands }) {
    return selectedBrands.some(({ brandCountryName }) => {
      // It's implicit that a falsy `brandCountryName` prop means the brand is US-based
      return !brandCountryName || brandCountryName === 'United States';
    });
  },
  selectedBrands(state, { activeViewEnabledBrands }) {
    return activeViewEnabledBrands.filter(b => b.checked);
  },
  selectedBrandIds(state, { selectedBrands }) {
    return selectedBrands.map(b => b.brandId);
  },
  benchmarksCategories(state) {
    return Object.values(state.categoriesAndSubcategories)
      .filter(c => c.subcategories);
  },
  demographicsEnabledPortfolioBrands(state) {
    return state.portfolioBrands.filter(b => b.isDashboardDemographicsEnabled);
  },
  salesInsightsEnabledPortfolioBrands(state) {
    return state.portfolioBrands.filter(b => b.enableSalesDashboard);
  },
  demographicsDashboardIsAvailable(_, { demographicsEnabledPortfolioBrands }) {
    return demographicsEnabledPortfolioBrands.some(brand => brand.checked);
  },
  salesDashboardIsAvailable(_, { salesInsightsEnabledPortfolioBrands }) {
    return salesInsightsEnabledPortfolioBrands.some(brand => brand.checked);
  },
};

export const moduleDashboard = {
  state,
  mutations,
  actions,
  getters,
};
